import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", credits: 100, price: 0, description: "", active: true, order: 0 };

export default function AdminPackages() {
  const [pkgs, setPkgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/credits/packages/admin").then(({ data }) => setPkgs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, credits: Number(form.credits), price: Number(form.price), order: Number(form.order) };
    try {
      if (editId) await api.patch(`/credits/packages/${editId}`, payload);
      else await api.post("/credits/packages", payload);
      toast.success("Pacote salvo");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/credits/packages/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (p) => { setForm(p); setEditId(p.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="admin-packages">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Pacotes de créditos</h2>
        <Button onClick={openNew} className="gap-1" data-testid="package-new-btn"><Plus className="h-4 w-4" /> Novo pacote</Button>
      </div>

      <MobileCards testid="pkg-cards">
        {pkgs.length === 0 && <EmptyCard>Nenhum pacote cadastrado.</EmptyCard>}
        {pkgs.map((p) => (
          <DataCard
            key={p.id}
            testid={`pkg-card-${p.id}`}
            title={p.name}
            subtitle={p.description}
            badge={<span className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.active ? "bg-green-500/15 text-green-600 dark:text-green-400" : "bg-muted text-muted-foreground"}`}>{p.active ? "Ativo" : "Inativo"}</span>}
            rows={[
              { label: "Créditos", value: <span className="font-num">{p.credits}</span> },
              { label: "Preço", value: <span className="font-num">R$ {Number(p.price).toFixed(2)}</span> },
            ]}
            actions={
              <>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(p)} data-testid={`pkg-edit-m-${p.id}`}>Editar</Button>
                <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`pkg-delete-m-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </>
            }
          />
        ))}
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nome</TableHead><TableHead>Créditos</TableHead><TableHead>Preço</TableHead><TableHead>Ativo</TableHead><TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pkgs.map((p) => (
              <TableRow key={p.id} data-testid={`pkg-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.credits}</TableCell>
                <TableCell>R$ {Number(p.price).toFixed(2)}</TableCell>
                <TableCell>{p.active ? "Sim" : "Não"}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`pkg-edit-${p.id}`}>Editar</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`pkg-delete-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="package-dialog">
          <DialogHeader><DialogTitle>{editId ? "Editar" : "Novo"} pacote</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pkg-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Créditos</Label><Input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="pkg-credits-input" /></div>
              <div className="space-y-2"><Label>Preço (R$)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pkg-price-input" /></div>
            </div>
            <div className="space-y-2"><Label>Descrição</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pkg-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-2"><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="pkg-order-input" /></div>
              <label className="flex items-center gap-2 py-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pkg-active-switch" /> Ativo</label>
            </div>
            <Button className="w-full" onClick={save} data-testid="pkg-save-btn">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
